import React, { useState, useMemo, useEffect } from "react";
import "./CampaignBuilder.css";
import CreateCampaign from "./CreateCampaign";
// ---- Pricing configuration ----
// Base price is per-day, scaled by every multiplier below.
const BASE_PRICE_PER_DAY = 225;

const AD_POSITIONS = [
    { id: "home", label: "Home Banner", multiplier: 1.0 },
    // { id: "search", label: "Search Top", multiplier: 1.25 },
    { id: "category", label: "Deals", multiplier: 0.9 },
    { id: "checkout", label: "Checkout", multiplier: 1.6 },
    { id: "splash", label: "In App PopUp", multiplier: 1.4 },
];

const CITIES = [
    { id: "mumbai", label: "Mumbai", multiplier: 1.3, reachBase: 9500 },
    { id: "delhi", label: "Delhi", multiplier: 1.25, reachBase: 9000 },
    { id: "bengaluru", label: "Bengaluru", multiplier: 1.2, reachBase: 8200 },
    { id: "hyderabad", label: "Hyderabad", multiplier: 1.0, reachBase: 7000 },
    { id: "panIndia", label: "Pan-India", multiplier: 2.4, reachBase: 26000 },
];

const BANNER_TYPES = [
    { id: "static", label: "Static", multiplier: 1.0 },
    { id: "animated", label: "Animated", multiplier: 1.35 },
    { id: "video", label: "Video", multiplier: 1.75 },
];

const PEAK_TIME_BOOST = 0.2; // +20%
const GST_RATE = 0.18;
const CLICK_THROUGH_RATE = 0.06; // estimated clicks as % of reach

const inr = (value) =>
    "₹" +
    Math.round(value).toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    });

export default function ParticularsectionBuilder({ selectedPlan, activeTab, onLaunchCampaign, hidePricingSection, hideParentBack, }) {
    const [days, setDays] = useState(13);
    // const [audienceSize, setAudienceSize] = useState(35);
    const [adPosition, setAdPosition] = useState("checkout");
    const [city, setCity] = useState("mumbai");
    const [bannerType, setBannerType] = useState("static");
    const [peakBoost, setPeakBoost] = useState(true);
    const [showCreateCampaign, setShowCreateCampaign] = useState(false);
    const [campaignData, setCampaignData] = useState(null);
    const getLeadCount = () => {
        if (!selectedPlan) return 100;

        const match = selectedPlan.title.match(/\d+/);
        return match ? Number(match[0]) : 100;
    };

    const [audienceSize, setAudienceSize] = useState(getLeadCount());
    const pricing = useMemo(() => {
        const positionCfg = AD_POSITIONS.find((p) => p.id === adPosition);
        const cityCfg = CITIES.find((c) => c.id === city);
        const bannerCfg = BANNER_TYPES.find((b) => b.id === bannerType);

        // Audience size scales cost linearly from 0.6x (small) to 2.0x (huge)
        const audienceMultiplier = 0.6 + (audienceSize / 100) * 1.4;

        let subtotal =
            BASE_PRICE_PER_DAY *
            days *
            audienceMultiplier *
            positionCfg.multiplier *
            cityCfg.multiplier *
            bannerCfg.multiplier;

        if (peakBoost) {
            subtotal *= 1 + PEAK_TIME_BOOST;
        }

        const gst = subtotal * GST_RATE;
        const total = subtotal + gst;

        // Estimated reach scales with city base reach, days, and audience %
        const estimatedReach =
            cityCfg.reachBase * days * (0.4 + audienceSize / 100) * positionCfg.multiplier * 0.5;

        const estimatedClicks = estimatedReach * CLICK_THROUGH_RATE * bannerCfg.multiplier;
        console.log("selectedPlan =", selectedPlan);
        return {
            subtotal,
            gst,
            total,
            estimatedReach: Math.round(estimatedReach),
            estimatedClicks: Math.round(estimatedClicks),
        };
    }, [days, audienceSize, adPosition, city, bannerType, peakBoost]);


    useEffect(() => {
        if (selectedPlan?.category === "leads") {
            const match = selectedPlan.title.match(/\d+/);

            if (match) {
                setAudienceSize(Number(match[0]));
            }
        }
    }, [selectedPlan]);

    return (
        <>
            {showCreateCampaign ? (
                <CreateCampaign
                    selectedPlan={campaignData}
                    selectedFeature={campaignData}
                    onBack={() => setShowCreateCampaign(false)}
                    onBack={() => setShowCreateCampaign(false)}
                />
            ) : (
                <section className="maamaasPricingWrapper">
                    {/* Pricing Cards */}
                    <div className="ccb-page">
                        <div className="ccb-wrap">
                            <header className="ccb-header">
                                <span className="ccb-eyebrow">Variable Cost Promotions</span>
                                <h1 className="ccb-title">Build a Custom Campaign</h1>
                                <p className="ccb-subtitle">
                                    Pricing changes based on duration, audience reach, ad position, banner type and peak time.
                                </p>
                            </header>

                            <div className="ccb-grid">
                                {/* ------------- LEFT PANEL: CONTROLS ------------- */}
                                <section className="ccb-card ccb-controls">
                                    {/* Number of Days */}
                                    <div className="ccb-field">
                                        <div className="ccb-field-row">
                                            <label htmlFor="days" className="ccb-label">
                                                Number of Days
                                            </label>
                                            <span className="ccb-value">{days} {days === 1 ? "day" : "days"}</span>
                                        </div>
                                        <input
                                            id="days"
                                            type="range"
                                            min={1}
                                            max={30}
                                            value={days}
                                            onChange={(e) => setDays(Number(e.target.value))}
                                            className="ccb-slider"
                                            style={{ "--fill": `${((days - 1) / (30 - 1)) * 100}%` }}
                                        />
                                    </div>

                                    {/* Audience Size */}
                                    {activeTab !== "digital" && (
                                        <div className="ccb-field">
                                            <div className="ccb-field-row">
                                                <label className="ccb-label">
                                                    {activeTab === "leads"
                                                        ? "Number of Leads"
                                                        : "Audience Size"}
                                                </label>

                                                <span className="ccb-value">
                                                    {activeTab === "leads"
                                                        ? `${audienceSize} Leads`
                                                        : `${audienceSize}%`}
                                                </span>
                                            </div>

                                            <input
                                                id="audience"
                                                type="range"
                                                min={1}
                                                max={100}
                                                value={audienceSize}
                                                onChange={(e) => setAudienceSize(Number(e.target.value))}
                                                className="ccb-slider"
                                                style={{ "--fill": `${audienceSize}%` }}
                                            />
                                        </div>
                                    )}
                                    {activeTab !== "digital" && (

                                        <div className="ccb-field">
                                            <span className="ccb-label ccb-block-label">Ad Position</span>
                                            <div className="ccb-pill-group">
                                                {AD_POSITIONS.map((pos) => (
                                                    <button
                                                        key={pos.id}
                                                        type="button"
                                                        className={`ccb-pill ${adPosition === pos.id ? "is-active" : ""}`}
                                                        onClick={() => setAdPosition(pos.id)}
                                                        aria-pressed={adPosition === pos.id}
                                                    >
                                                        {pos.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* City */}
                                    <div className="ccb-field">
                                        <span className="ccb-label ccb-block-label">City</span>
                                        <div className="ccb-pill-group">
                                            {CITIES.map((c) => (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    className={`ccb-pill ${city === c.id ? "is-active" : ""}`}
                                                    onClick={() => setCity(c.id)}
                                                    aria-pressed={city === c.id}
                                                >
                                                    {c.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Banner Type + Peak Time Boost */}
                                    <div className="ccb-field ccb-split">
                                        <div className="ccb-split-col">
                                            <span className="ccb-label ccb-block-label">Banner Type</span>
                                            <div className="ccb-pill-group">
                                                {BANNER_TYPES.map((b) => (
                                                    <button
                                                        key={b.id}
                                                        type="button"
                                                        className={`ccb-pill ${bannerType === b.id ? "is-active" : ""}`}
                                                        onClick={() => setBannerType(b.id)}
                                                        aria-pressed={bannerType === b.id}
                                                    >
                                                        {b.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="ccb-split-col">
                                            <span className="ccb-label ccb-block-label">Peak Time Boost</span>
                                            <button
                                                type="button"
                                                className={`ccb-toggle ${peakBoost ? "is-on" : "is-off"}`}
                                                onClick={() => setPeakBoost((v) => !v)}
                                                aria-pressed={peakBoost}
                                            >
                                                {peakBoost ? "Enabled (+20%)" : "Disabled"}
                                            </button>
                                        </div>
                                    </div>
                                </section>

                                {/* ------------- RIGHT PANEL: SUMMARY ------------- */}
                                <aside className="ccb-card ccb-summary">
                                    <span className="ccb-summary-tag">
                                        <svg
                                            className="ccb-summary-icon"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
                                            <line x1="8" y1="7.5" x2="16" y2="7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            <line x1="8" y1="11.5" x2="10.5" y2="11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            <line x1="13.5" y1="11.5" x2="16" y2="11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            <line x1="8" y1="15.5" x2="10.5" y2="15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            <line x1="13.5" y1="15.5" x2="16" y2="15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        </svg>
                                        Dynamic Pricing
                                    </span>

                                    <div className="ccb-total-block">
                                        <span className="ccb-total-label">Total Amount</span>
                                        <span className="ccb-total-value">{inr(pricing.total)}</span>
                                    </div>

                                    <dl className="ccb-stat-list">
                                        <div className="ccb-stat-row">
                                            <dt>Estimated Reach</dt>
                                            <dd>{pricing.estimatedReach.toLocaleString("en-IN")}</dd>
                                        </div>
                                        <div className="ccb-stat-row">
                                            <dt>Estimated Clicks</dt>
                                            <dd>{pricing.estimatedClicks.toLocaleString("en-IN")}</dd>
                                        </div>
                                        <div className="ccb-stat-row">
                                            <dt>Subtotal</dt>
                                            <dd>{inr(pricing.subtotal)}</dd>
                                        </div>
                                        <div className="ccb-stat-row">
                                            <dt>GST (18%)</dt>
                                            <dd>{inr(pricing.gst)}</dd>
                                        </div>
                                    </dl>

                                    <div className="ccb-final-row">
                                        <span className="ccb-final-label">Final Amount</span>
                                        <span className="ccb-final-value">{inr(pricing.total)}</span>
                                    </div>

                                    <button
                                        type="button"
                                        className="ccb-launch-btn"
                                        onClick={() => {
                                            hidePricingSection?.();
                                            hideParentBack?.();
                                            const data = selectedPlan || {
                                                goal: "branding",
                                                subGoal: "awareness",
                                                title: "Branding Campaign",
                                                category: "branding",
                                            };

                                            console.log("Opening CreateCampaign:", data);

                                            setCampaignData(data);
                                            setShowCreateCampaign(true);
                                        }}
                                    >
                                        Launch this Campaign
                                    </button>
                                </aside>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}